import {
  NormalizedDataRow,
  PlatformDetectionResult,
  ColumnMappingResult,
  UploadStaging,
  Platform
} from '@/types'
import validator from 'validator'
import { parseMoneyAmount } from '@/lib/money'

/**
 * Data Extraction Service
 *
 * Transforms raw CSV data into normalized JSON structure for UploadStaging.
 * This is the bridge between raw file data and the structured processing pipeline.
 */

export interface DataExtractionResult {
  normalizedData: NormalizedDataRow[]
  extractedCount: number
  errorCount: number
  errors: Array<{
    rowIndex: number
    error: string
    originalData: any
  }>
}

export interface EarningsValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export class DataExtractionService {
  /**
   * Create basic extraction result for staging workflow (no column mapping required)
   * This preserves the raw data as-is for the field mapping UI to handle
   */
  static createBasicExtractionResult(
    rawData: any[],
    headers: string[],
    platformInfo: PlatformDetectionResult,
    platform: Platform
  ): DataExtractionResult {
    // Convert raw data to basic normalized format without any field mapping
    const normalizedData: NormalizedDataRow[] = rawData.map((row, index) => ({
      rowIndex: index,
      originalData: row,
      extractedData: {
        driverIdentifiers: {
          // Empty until field mapping is done
        },
        earningsData: {
          grossEarnings: 0,
          netEarnings: 0,
          tips: 0
          // Empty defaults until field mapping is done
        }
      }
    }))

    return {
      normalizedData,
      extractedCount: normalizedData.length,
      errorCount: 0,
      errors: []
    }
  }

  /**
   * Extract and normalize data from raw CSV rows (used after field mapping)
   */
  static extractData(
    rawData: any[],
    headers: string[],
    platformInfo: PlatformDetectionResult,
    columnMapping: ColumnMappingResult,
    platform: Platform
  ): DataExtractionResult {
    // Check if this is SumUp transaction data that needs aggregation
    if (platform === 'sumup' &&
        columnMapping.identifierFields.transactionId &&
        columnMapping.earningsFields.description) {
      return this.extractSumUpAggregatedData(rawData, columnMapping)
    }

    // Standard extraction for non-SumUp data
    const normalizedData: NormalizedDataRow[] = []
    const errors: Array<{ rowIndex: number; error: string; originalData: any }> = []

    rawData.forEach((row, index) => {
      try {
        // Skip company/accounting rows for Uber
        if (platform === 'uber' && this.isUberCompanyRow(row, columnMapping)) {
          return
        }

        const normalizedRow = this.extractSingleRow(row, index, columnMapping)
        normalizedData.push(normalizedRow)
      } catch (error) {
        console.error(`[DataExtraction] Error processing row ${index}:`, error)
        errors.push({
          rowIndex: index,
          error: error instanceof Error ? error.message : 'Unknown extraction error',
          originalData: row
        })
      }
    })

    const extractedCount = normalizedData.length
    const errorCount = errors.length

    return {
      normalizedData,
      extractedCount,
      errorCount,
      errors
    }
  }

  /**
   * Extract data from a single row
   */
  private static extractSingleRow(
    row: any,
    rowIndex: number,
    columnMapping: ColumnMappingResult
  ): NormalizedDataRow {
    // Extract driver identifiers
    const driverIdentifiers = this.extractDriverIdentifiers(row, columnMapping)

    // Extract earnings data
    const earningsData = this.extractEarningsData(row, columnMapping)

    return {
      rowIndex,
      originalData: row,
      extractedData: {
        driverIdentifiers,
        earningsData
      }
    }
  }

  /**
   * Extract driver identifiers from a row
   */
  private static extractDriverIdentifiers(
    row: any,
    columnMapping: ColumnMappingResult
  ): {
    name?: string
    uuid?: string
    email?: string
    phone?: string
  } {
    const identifiers: {
      name?: string
      uuid?: string
      email?: string
      phone?: string
    } = {}

    // Extract name
    const name = this.extractName(row, columnMapping.identifierFields)
    if (name) identifiers.name = name

    // Extract UUID/ID
    const uuid = this.extractUUID(row, columnMapping.identifierFields)
    if (uuid) identifiers.uuid = uuid

    // Extract email
    const email = this.extractEmail(row, columnMapping.identifierFields)
    if (email) identifiers.email = email

    // Extract phone
    const phone = this.extractPhone(row, columnMapping.identifierFields)
    if (phone) identifiers.phone = phone

    return identifiers
  }

  /**
   * Extract earnings data from a row
   */
  private static extractEarningsData(
    row: any,
    columnMapping: ColumnMappingResult
  ): {
    grossEarnings: number
    netEarnings: number
    tips: number
    driverBonus?: number
    actualVAT?: number
    cashReceived?: number
  } {
    const data: {
      grossEarnings: number
      netEarnings: number
      tips: number
      driverBonus?: number
      actualVAT?: number
      cashReceived?: number
    } = {
      grossEarnings: 0,
      netEarnings: 0,
      tips: 0
    }

    const { earningsFields } = columnMapping

    // Extract each earnings field
    if (earningsFields.grossEarnings && row[earningsFields.grossEarnings] !== undefined) {
      data.grossEarnings = this.parseNumber(row[earningsFields.grossEarnings])
    }

    if (earningsFields.netEarnings && row[earningsFields.netEarnings] !== undefined) {
      data.netEarnings = this.parseNumber(row[earningsFields.netEarnings])
    }

    if (earningsFields.tips && row[earningsFields.tips] !== undefined) {
      data.tips = this.parseNumber(row[earningsFields.tips])
    }

    // Only include optional fields if they have values
    if (earningsFields.driverBonus && row[earningsFields.driverBonus] !== undefined) {
      const driverBonus = this.parseNumber(row[earningsFields.driverBonus])
      if (driverBonus > 0) data.driverBonus = driverBonus
    }

    if (earningsFields.actualVAT && row[earningsFields.actualVAT] !== undefined) {
      const actualVAT = this.parseNumber(row[earningsFields.actualVAT])
      if (actualVAT > 0) data.actualVAT = actualVAT
    }

    if (earningsFields.cashReceived && row[earningsFields.cashReceived] !== undefined) {
      const cashReceived = this.parseNumber(row[earningsFields.cashReceived])
      if (cashReceived > 0) data.cashReceived = cashReceived
    }

    return data
  }

  /**
   * Extract driver name from row
   */
  private static extractName(
    row: any,
    identifierFields: Record<string, string>
  ): string | undefined {
    // Strategy 1: Use full name field if available
    if (identifierFields.driverName && row[identifierFields.driverName]) {
      const name = this.cleanIdentifier(row[identifierFields.driverName])
      if (name && this.isValidName(name)) {
        return name
      }
    }

    // Strategy 2: Combine first and last name
    if (identifierFields.firstName && identifierFields.lastName) {
      const firstName = this.cleanIdentifier(row[identifierFields.firstName])
      const lastName = this.cleanIdentifier(row[identifierFields.lastName])

      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`
        if (this.isValidName(fullName)) {
          return fullName
        }
      }
    }

    // Strategy 3: Use first name only if available
    if (identifierFields.firstName && row[identifierFields.firstName]) {
      const firstName = this.cleanIdentifier(row[identifierFields.firstName])
      if (firstName && this.isValidName(firstName)) {
        return firstName
      }
    }

    // Strategy 4: Use last name only if available
    if (identifierFields.lastName && row[identifierFields.lastName]) {
      const lastName = this.cleanIdentifier(row[identifierFields.lastName])
      if (lastName && this.isValidName(lastName)) {
        return lastName
      }
    }

    return undefined
  }

  /**
   * Extract UUID/ID from row
   */
  private static extractUUID(
    row: any,
    identifierFields: Record<string, string>
  ): string | undefined {
    if (identifierFields.driverId && row[identifierFields.driverId]) {
      const uuid = this.cleanIdentifier(row[identifierFields.driverId])
      if (uuid && this.isValidUUID(uuid)) {
        return uuid
      }
    }

    return undefined
  }

  /**
   * Extract email from row
   */
  private static extractEmail(
    row: any,
    identifierFields: Record<string, string>
  ): string | undefined {
    if (identifierFields.email && row[identifierFields.email]) {
      const email = this.cleanIdentifier(row[identifierFields.email])
      if (email && validator.isEmail(email)) {
        return email
      }
    }

    return undefined
  }

  /**
   * Extract phone from row
   */
  private static extractPhone(
    row: any,
    identifierFields: Record<string, string>
  ): string | undefined {
    if (identifierFields.phone && row[identifierFields.phone]) {
      const phone = this.cleanIdentifier(row[identifierFields.phone])
      if (phone && this.isValidPhone(phone)) {
        return phone
      }
    }

    return undefined
  }

  /**
   * Safely parse numeric values from CSV data.
   * Platform exports use absolute amounts; sign is dropped and
   * unparseable values fall back to 0.
   */
  private static parseNumber(value: any): number {
    const parsed = parseMoneyAmount(value)
    return parsed === null ? 0 : Math.abs(parsed)
  }

  /**
   * Clean and validate extracted identifier
   */
  private static cleanIdentifier(value: any): string | null {
    if (!value || typeof value !== 'string') {
      return null
    }

    const cleaned = String(value).trim()
    if (cleaned === '' || cleaned.toLowerCase() === 'null' || cleaned.toLowerCase() === 'undefined') {
      return null
    }

    return cleaned
  }

  /**
   * Validate if a value looks like a name
   */
  private static isValidName(value: string): boolean {
    if (!value) return false
    const str = value.trim()

    // Basic checks: not empty, contains letters, reasonable length
    if (str.length < 2 || str.length > 100) return false
    if (!/[a-zA-ZåäöÅÄÖ]/.test(str)) return false

    // Not a number or UUID-like string
    if (/^\d+$/.test(str)) return false
    if (/^[0-9a-f-]{8,}$/i.test(str)) return false

    return true
  }

  /**
   * Validate if a value looks like a UUID
   */
  private static isValidUUID(value: string): boolean {
    if (!value) return false
    const str = value.trim()

    // Standard UUID format (with or without dashes)
    const uuidRegex = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i
    if (uuidRegex.test(str)) return true

    // Alphanumeric identifiers (8+ characters, common in ride-sharing platforms)
    if (/^[0-9a-zA-Z]{8,}$/.test(str)) return true

    // Numeric identifiers (8+ digits)
    if (/^\d{8,}$/.test(str)) return true

    // Hex-like identifiers with dashes (minimum 8 characters)
    if (/^[0-9a-f-]{8,}$/i.test(str) && str.length >= 8) return true

    return false
  }

  /**
   * Validate if a value looks like a phone number
   */
  private static isValidPhone(value: string): boolean {
    if (!value) return false
    const str = value.trim()

    // Swedish phone number patterns
    return validator.isMobilePhone(str, 'sv-SE') ||
           /^(\+46|0)[0-9]{8,9}$/.test(str.replace(/[\s-]/g, ''))
  }

  /**
   * Check if row is a company/accounting summary row (Uber-specific)
   * Company rows are missing both grossEarnings and netEarnings
   */
  private static isUberCompanyRow(row: any, columnMapping: ColumnMappingResult): boolean {
    const { earningsFields } = columnMapping

    // Check if grossEarnings has a valid non-zero value
    const hasGrossEarnings = earningsFields.grossEarnings &&
      row[earningsFields.grossEarnings] &&
      this.parseNumber(row[earningsFields.grossEarnings]) > 0

    // Check if netEarnings has a valid non-zero value
    const hasNetEarnings = earningsFields.netEarnings &&
      row[earningsFields.netEarnings] &&
      this.parseNumber(row[earningsFields.netEarnings]) > 0

    // Company row if missing BOTH grossEarnings AND netEarnings
    return !hasGrossEarnings && !hasNetEarnings
  }

  /**
   * Validate extracted earnings data
   */
  static validateEarningsData(data: {
    grossEarnings: number
    netEarnings: number
    tips: number
    driverBonus?: number
    actualVAT?: number
  }): EarningsValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    // Basic validation
    if (data.grossEarnings < 0) {
      errors.push('Bruttoinkomst kan inte vara negativ')
    }

    if (data.netEarnings < 0) {
      errors.push('Nettoinkomst kan inte vara negativ')
    }

    if (data.tips < 0) {
      errors.push('Dricks kan inte vara negativ')
    }

    if (data.driverBonus && data.driverBonus < 0) {
      errors.push('Förarbonus kan inte vara negativ')
    }

    if (data.actualVAT && data.actualVAT < 0) {
      errors.push('Moms kan inte vara negativ')
    }

    // Logical validation
    if (data.grossEarnings > 0 && data.netEarnings > data.grossEarnings) {
      warnings.push('Nettoinkomst är högre än bruttoinkomst')
    }

    if (data.grossEarnings === 0 && data.netEarnings === 0) {
      warnings.push('Både brutto- och nettoinkomst är noll')
    }

    // VAT validation
    if (data.actualVAT && data.grossEarnings > 0 && data.actualVAT > data.grossEarnings) {
      warnings.push('Moms är högre än bruttoinkomst')
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    }
  }


  /**
   * Extract and aggregate SumUp transaction data using 3-step process
   */
  private static extractSumUpAggregatedData(
    rawData: any[],
    columnMapping: ColumnMappingResult
  ): DataExtractionResult {
    const transactionIdField = columnMapping.identifierFields.transactionId!
    const descriptionField = columnMapping.earningsFields.description!
    const driverNameField = columnMapping.identifierFields.driverName!
    const grossEarningsField = columnMapping.earningsFields.grossEarnings!

    // ============================================================================
    // STEP 1: CATEGORIZE TRANSACTIONS AS TIP OR EARNING
    // ============================================================================
    const classifiedTransactions: Array<{
      type: 'tip' | 'earning'
      transactionId: string
      driverName: string
      grossEarnings: number
      originalData: any
    }> = []
    let tipCount = 0
    let earningCount = 0

    rawData.forEach(row => {
      const description = row[descriptionField]
      const transactionId = row[transactionIdField]
      const driverName = row[driverNameField]
      const grossEarnings = this.parseNumber(row[grossEarningsField])

      // Every row that is not a tip is an earning
      const isTip = description && typeof description === 'string' &&
                    description.toLowerCase().includes('tip')

      classifiedTransactions.push({
        type: isTip ? 'tip' : 'earning',
        transactionId: transactionId?.toString().trim() || '',
        driverName: driverName?.toString().trim() || '',
        grossEarnings,
        originalData: row
      })

      if (isTip) {
        tipCount++
      } else {
        earningCount++
      }
    })

    // ============================================================================
    // STEP 2: GROUP BY TRANSACTION ID, USE GROSS EARNINGS AS VALUES AND CATEGORY AS KEYS
    // ============================================================================
    const transactionGroups: Array<{
      transactionId: string
      driverName: string
      tips: number
      earnings: number
      earningRecord: any // Keep the earning record for driver name and other fields
    }> = []

    // Group by transaction ID
    const tempGroups = new Map<string, {
      tips: number
      earnings: number
      driverName: string
      earningRecord?: any
    }>()

    classifiedTransactions.forEach(classified => {
      const { transactionId, type, grossEarnings, driverName, originalData } = classified

      if (!transactionId) {
        return
      }

      if (!tempGroups.has(transactionId)) {
        tempGroups.set(transactionId, {
          tips: 0,
          earnings: 0,
          driverName: '',
          earningRecord: null
        })
      }

      const group = tempGroups.get(transactionId)!

      if (type === 'tip') {
        group.tips += grossEarnings
      } else {
        // This is an earning
        group.earnings += grossEarnings
        // Use driver name from the earning (not from tips)
        group.driverName = driverName
        group.earningRecord = originalData
      }
    })

    // Convert to transaction groups (skip transactions with no earnings record)
    tempGroups.forEach((group, transactionId) => {
      if (!group.earningRecord) {
        return
      }

      if (!group.driverName) {
        return
      }

      transactionGroups.push({
        transactionId,
        driverName: group.driverName,
        tips: group.tips,
        earnings: group.earnings,
        earningRecord: group.earningRecord
      })
    })

    // ============================================================================
    // STEP 3: AGGREGATE BY DRIVER NAME
    // ============================================================================
    const driverGroups = new Map<string, Array<{
      transactionId: string
      tips: number
      earnings: number
      earningRecord: any
    }>>()

    // Group transaction groups by driver name
    transactionGroups.forEach(group => {
      if (!driverGroups.has(group.driverName)) {
        driverGroups.set(group.driverName, [])
      }
      driverGroups.get(group.driverName)!.push(group)
    })

    // Create aggregated driver records
    const normalizedData: NormalizedDataRow[] = []
    let rowIndex = 0

    driverGroups.forEach((groups, driverName) => {
      // Aggregate all transactions for this driver
      let totalEarnings = 0
      let totalTips = 0
      let transactionCount = 0
      const firstEarningRecord = groups[0].earningRecord

      groups.forEach((group) => {
        totalEarnings += group.earnings
        totalTips += group.tips
        transactionCount++
      })

      // ============================================================================
      // SUMUP HACK: Adapt to Bolt/Uber tips logic
      // ============================================================================
      // In Bolt/Uber: tips are INCLUDED in netEarnings, then SUBTRACTED during salary calculations
      // In SumUp: tips come separately and need to be "artificially included" to match this logic
      //
      // Solution: Add tips to both grossEarnings and netEarnings, while keeping tips field separate
      // This way:
      //   1. grossEarnings = earnings + tips (tips are "included" since SumUp has no commission)
      //   2. netEarnings = earnings + tips (tips are "included")
      //   3. tips field = tips (so they can be "subtracted" later)
      //   4. The salary calculation service will see the same pattern as Bolt/Uber
      // ============================================================================
      const totalWithTips = totalEarnings + totalTips

      // Create normalized row for aggregated driver data
      // Both gross and net should include tips since SumUp has no commission (gross = net)
      const aggregatedRow: Record<string, any> = {
        [driverNameField]: driverName,
        [grossEarningsField]: totalWithTips.toString(), // Include tips in gross
      }

      // Set net earnings to include tips (matching Bolt/Uber pattern)
      if (columnMapping.earningsFields.netEarnings) {
        aggregatedRow[columnMapping.earningsFields.netEarnings] = totalWithTips.toString()
      }

      // Add tips field separately (will be subtracted during salary calculation)
      if (columnMapping.earningsFields.tips) {
        aggregatedRow[columnMapping.earningsFields.tips] = totalTips.toString()
      }

      // Copy other identifier fields from first earning record
      Object.entries(columnMapping.identifierFields).forEach(([, fieldName]) => {
        if (fieldName && fieldName !== driverNameField && firstEarningRecord[fieldName]) {
          aggregatedRow[fieldName] = firstEarningRecord[fieldName]
        }
      })

      // Directly construct normalized row with our calculated values
      // Don't use extractSingleRow as it causes column mapping conflicts
      // (both grossEarnings and tips map to same column "Pris (brutto)")
      const normalizedRow: NormalizedDataRow = {
        rowIndex,
        originalData: aggregatedRow,
        extractedData: {
          driverIdentifiers: {
            name: driverName
          },
          earningsData: {
            grossEarnings: totalWithTips,  // earnings + tips (SumUp has no commission)
            netEarnings: totalWithTips,     // same as gross
            tips: totalTips                 // separate for salary calculations
          }
        }
      }
      normalizedData.push(normalizedRow)
      rowIndex++
    })

    return {
      normalizedData,
      extractedCount: normalizedData.length,
      errorCount: 0,
      errors: []
    }
  }

  /**
   * Create UploadStaging entry from extracted data
   */
  static createUploadStaging(
    uploadId: string,
    platform: Platform,
    period: string,
    normalizedData: NormalizedDataRow[],
    platformInfo: PlatformDetectionResult,
    columnMapping?: ColumnMappingResult
  ): Omit<UploadStaging, 'id'> {
    const staging: Omit<UploadStaging, 'id'> = {
      uploadId,
      platform,
      period,
      normalizedData,
      platformInfo,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    }

    // Only include columnMapping if provided
    if (columnMapping) {
      staging.columnMapping = columnMapping
    }

    return staging
  }
}